import pluralize from 'pluralize'
import toast from 'react-hot-toast'

import { useMutation, useQueryClient } from '@fv/client-core'
import type { Quote } from '@fv/client-types'
import type { UpsertOpportunityQuotesDto } from '@fv/models'

import { useCacheHelpers } from '@/hooks/opportunities'
import {
  messagesQueryKeys,
  opportunityQueryKeys,
  quoteQueryKeys,
  quoteRequestKeys,
} from '@/queryKeys'
import { carrierFetch } from '@/utils/carrierFetch'

type SubmitQuotesVariables = UpsertOpportunityQuotesDto & {
  isUpdate?: boolean
}

const submitQuotesForOpportunity =
  (loadId: string) =>
  async ({ message, quotes }: SubmitQuotesVariables) => {
    return await carrierFetch<Quote[]>(`/loads/${loadId}/quotes`, {
      method: 'PUT',
      body: { message, quotes },
    })
  }

type HookOpts = {
  loadId: string
  quoteRequestId: string
}
export function useSubmitQuotes({ loadId, quoteRequestId }: HookOpts) {
  const { refreshBucketCounts } = useCacheHelpers()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: submitQuotesForOpportunity(loadId),
    onSuccess: (quotes, variables) => {
      const isNewQuote = !variables.isUpdate

      if (isNewQuote) {
        queryClient.invalidateQueries({
          queryKey: opportunityQueryKeys.byStatus('offered'),
        })
        queryClient.invalidateQueries({
          queryKey: opportunityQueryKeys.byStatus('open'),
        })
        refreshBucketCounts()
      }

      queryClient.setQueryData(quoteQueryKeys.load(loadId), quotes)

      const message = isNewQuote
        ? 'Quote added successfully.'
        : `${pluralize('Quote', variables.quotes.length)} updated successfully.`

      toast.success(message)

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: messagesQueryKeys.load(loadId),
        })
        queryClient.invalidateQueries({
          queryKey: quoteRequestKeys.id(quoteRequestId),
        })
      })
    },
  })
}
